
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import ProductCard, { ProductCardProps } from '@/components/product/card/ProductCard';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

// This interface now directly reflects the structure of data returned by fetchRecentListings from Index.tsx
// (which includes snake_case fields from the database and specific object structures for categories/images)
interface ListingDataWithProfile {
  id: number;
  title: string;
  price: number;
  location: string;
  images: string[] | null; // Array of image URLs from DB
  is_premium: boolean; // snake_case from DB
  created_at: string; // snake_case from DB
  updated_at?: string; // snake_case from DB, optional as it might not be selected
  condition: string | null;
  delivery_option: string | null; // snake_case from DB
  user_id: string | null;
  stock: number | null;
  is_price_negotiable: boolean;
  make: string | null;
  model: string | null;
  year: number | null;
  mileage: number | null;
  transmission: string | null;
  categories: { name: string } | null; // Object from DB
  subcategories: { name: string } | null; // Object from DB
  profiles: { username: string | null; profile_image: string | null; } | null;
}


interface FeaturedListingsProps {
  title: string;
  fetchListings: (page: number, limit: number) => Promise<{
    data: ListingDataWithProfile[]; // This type now matches ListingWithProfile from Index.tsx
    hasMore: boolean;
  }>;
  buttonLink: string;
  buttonText: string;
  infiniteScroll?: boolean;
  itemsPerPage?: number;
}

const FeaturedListings = ({
  title,
  fetchListings,
  buttonLink,
  buttonText,
  infiniteScroll = false,
  itemsPerPage = 10,
}: FeaturedListingsProps) => {
  const [listings, setListings] = useState<ListingDataWithProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const observerTarget = useRef<HTMLDivElement>(null);

  const loadListings = useCallback(
    async (pageNum: number) => {
      try {
        if (pageNum === 0) setIsLoading(true);
        else setIsPaginationLoading(true);

        setError(null);

        const result = await fetchListings(pageNum, itemsPerPage);

        setListings((prev) =>
          pageNum === 0 ? result.data : [...prev, ...result.data]
        );

        setHasMore(result.hasMore);
      } catch (err) {
        setError("Could not load listings. Please try again later.");
        if (pageNum === 0) setListings([]);
      } finally {
        if (pageNum === 0) setIsLoading(false);
        else setIsPaginationLoading(false);
      }
    },
    [fetchListings, itemsPerPage]
  );

  useEffect(() => {
    loadListings(page);
  }, [page, loadListings]);

  useEffect(() => {
    if (!infiniteScroll) return;
    if (!hasMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !isLoading &&
          !isPaginationLoading
        ) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 0.1, rootMargin: "100px" }
    );
    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);
    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [hasMore, isLoading, isPaginationLoading, infiniteScroll]);

  if (error) {
    return (
      <div className="container py-12">
        <h2 className="text-3xl font-bold mb-4">{title}</h2>
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          {error}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container py-12">
        <h2 className="text-3xl font-bold mb-4">{title}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="animate-pulse rounded-md bg-gray-200 dark:bg-gray-800 h-64"
            ></div>
          ))}
        </div>
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div className="container py-12">
        <h2 className="text-3xl font-bold mb-4">{title}</h2>
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-md text-gray-600">
          No listings found.
        </div>
      </div>
    );
  }

  return (
    <div className="py-12">
      <div className="flex justify-between items-center mb-6 pb-2 border-b border-gray-100">
        <h2 className="text-3xl font-bold">{title}</h2>
        {!infiniteScroll && (
          <Link to={buttonLink}>
            <Button
              variant="default"
              className="bg-brand-500 hover:bg-brand-600"
            >
              {buttonText}
            </Button>
          </Link>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {listings.map((listing) => (
          <ProductCard
            key={`${listing.id}-${listing.updated_at || listing.created_at}`} // uses snake_case created_at or updated_at
            id={listing.id.toString()}
            title={listing.title}
            price={listing.price}
            location={listing.location}
            imageUrl={listing.images?.[0] || ""} // Mapped from images array
            category={listing.categories?.name || ""} // Mapped from categories object
            subcategory={listing.subcategories?.name || ""} // Mapped from subcategories object
            isPremium={listing.is_premium} // Mapped from snake_case
            createdAt={listing.created_at} // Mapped from snake_case
            condition={listing.condition || undefined}
            deliveryOption={listing.delivery_option || "shipping_and_pickup"} // Mapped from snake_case
            user_id={listing.user_id || undefined}
            stock={listing.stock === null ? undefined : listing.stock}
            is_price_negotiable={listing.is_price_negotiable}
            make={listing.make || undefined}
            model={listing.model || undefined}
            year={listing.year === null ? undefined : listing.year}
            mileage={listing.mileage === null ? undefined : listing.mileage}
            transmission={listing.transmission || undefined}
            username={listing.profiles?.username}
            userProfileImage={listing.profiles?.profile_image}
          />
        ))}
      </div>

      {infiniteScroll && (
        <div
          ref={observerTarget}
          className="w-full py-8 flex justify-center"
          data-testid="infinite-scroll-trigger"
        >
          {isPaginationLoading && (
            <div className="flex flex-col items-center">
              <LoadingSpinner size="md" />
              <p className="text-gray-500 mt-2">Loading more listings...</p>
            </div>
          )}
          {!hasMore && listings.length > 0 && (
            <p className="text-gray-500 py-2 px-4 border-t border-gray-200">
              You've seen all listings
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default FeaturedListings;

